import React, {Component} from 'react';
import classNames from 'classnames';
import format from 'date-fns/format';
import StarRatings from 'react-star-ratings';
import {createReview} from '../api/yotpoClient';
import pluralize from '../utils/pluralize';

const MAX_STARS = 5;
const STAR_SVG_SHAPE = 'M11.5384 16.5622L4.74 20L6.05965 12.7573L0.538379 7.63932L8.15234 6.60089L11.5384 0L14.9244 6.60089L22.5384 7.63932L17.0171 12.7573L18.3368 20L11.5384 16.5622Z';
const STAR_SVG_BOX = '0 0 23 20';
const STAR_SIZE = '19px';
const STAR_SPACING = '3px';

const Review = ({
	rating,
	title,
	content,
	author,
	date,
	verified,
}) => (
	<div className="bt--gray-border pt4 pb4">
		<div className="row">
			<div className="col c3--md mb3">
				<p className="domaine--small mb1">{author}</p>
				<p className="sans--xsmall uppercase fw--600 color--gray-text">{format(date, 'MM/dd/yy')}</p>
				{verified && (
					<p className="mt1 sans--small uppercase fw--600">Verified Buyer</p>
				)}
			</div>
			<div className="col c6--md">
				<div className="mb1">
					<StarRatings
						rating={rating}
						numberOfStars={MAX_STARS}
						starRatedColor="#000"
						starEmptyColor="#fff"
						svgIconPath={STAR_SVG_SHAPE}
						svgIconViewBox={STAR_SVG_BOX}
						starDimension={STAR_SIZE}
						starSpacing={STAR_SPACING}
					/>
				</div>
				<h3
					className="domaine--medium mb1"
					dangerouslySetInnerHTML={{__html: title}}
				/>
				<div
					className="rich-text--page"
					dangerouslySetInnerHTML={{__html: content}}
				/>
			</div>
		</div>
	</div>
);

const INITIAL_STATE = {
	showReviewForm: false,
	isSubmitting: false,
	isSuccess: false,
	pendingRating: 0,
	pendingName: '',
	pendingEmail: '',
	pendingTitle: '',
	pendingContent: '',
};

export default class ProductReviews extends Component {
	constructor() {
		super();

		this.state = {
			...INITIAL_STATE,
		};
	}

	toggleForm = () => {
		this.setState({showReviewForm: !this.state.showReviewForm});
	}

	setFormValue = (key, value) => {
		this.setState({[key]: value});
	}

	onSubmit = async event => {
		const {
			productId,
			productTitle,
			productUrl,
		} = this.props;

		const {
			pendingName,
			pendingRating,
			pendingContent,
			pendingTitle,
			pendingEmail,
		} = this.state;

		event.preventDefault();

		this.setState({
			isSubmitting: true,
		});

		try {
			await createReview({
				sku: productId.toString(),
				product_title: productTitle,
				product_url: productUrl,
				email: pendingEmail,
				display_name: pendingName,
				review_content: pendingContent,
				review_score: pendingRating,
				review_title: pendingTitle,
			});

			this.setState({
				showReviewForm: false,
				isSubmitting: false,
				isSuccess: true,
				pendingRating: 0,
				pendingName: '',
				pendingEmail: '',
				pendingTitle: '',
				pendingContent: '',
			});
		} catch (error) {
			console.log(error);

			this.setState({
				isSubmitting: false,
			});
		}
	}

	render() {
		const {
			reviews = [],
			bottomLine,
		} = this.props;

		const {
			showReviewForm,
			isSubmitting,
			isSuccess,
			pendingRating,
			pendingName,
			pendingEmail,
			pendingTitle,
			pendingContent,
		} = this.state;

		const isSubmitDisabled = (
			!pendingRating ||
			!pendingName ||
			!pendingEmail ||
			!pendingTitle ||
			!pendingContent
		);

		const reviewRows = reviews.map(review => (
			<Review
				key={review.id}
				rating={review.score}
				title={review.title}
				author={review.name}
				content={review.content}
				date={new Date(review.created_at)}
				verified={review.reviewer_type === 'verified_buyer'}
			/>
		));

		const emptyState = (reviews.length === 0) && (
			<div className="bt--gray-border pt4 pt12--md pb4">
				<h3 className="domaine--medium mb1">
					Currently No Reviews
				</h3>
				<div className="rich-text--page">
					We’re sorry, there are currently no reviews for this product. Be the first to review!
				</div>
			</div>
		);

		const pendingForm = (showReviewForm && !isSuccess) && (
			<form
				onSubmit={this.onSubmit}
				className="db bt--gray-border pt4 pb4 mb4">
				<div className="mb2">
					<span className="mr1 color--gray-text">
						Rating:
					</span>
					<StarRatings
						rating={pendingRating}
						numberOfStars={MAX_STARS}
						starRatedColor="#000"
						starHoverColor="#000"
						starEmptyColor="#fff"
						svgIconPath={STAR_SVG_SHAPE}
						svgIconViewBox={STAR_SVG_BOX}
						starDimension={STAR_SIZE}
						starSpacing={STAR_SPACING}
						changeRating={pendingRating => this.setFormValue('pendingRating', pendingRating)}
					/>
				</div>
				<div className="row">
					<div className="col c6--md mb2">
						<input
							disabled={isSubmitting}
							className={classNames('input--primary full-width', {
								loading: isSubmitting
							})}
							type="text"
							placeholder="Full Name"
							onChange={event => this.setFormValue('pendingName', event.target.value)}
							value={pendingName}
						/>
					</div>
					<div className="col c6--md mb2">
						<input
							disabled={isSubmitting}
							className={classNames('input--primary full-width', {
								loading: isSubmitting
							})}
							type="email"
							placeholder="Email"
							onChange={event => this.setFormValue('pendingEmail', event.target.value)}
							value={pendingEmail}
						/>
					</div>
					<div className="col mb4">
						<input
							disabled={isSubmitting}
							className={classNames('input--primary full-width', {
								loading: isSubmitting
							})}
							type="text"
							placeholder="Review Title"
							onChange={event => this.setFormValue('pendingTitle', event.target.value)}
							value={pendingTitle}
						/>
					</div>
					<div className="col mb3">
						<textarea
							disabled={isSubmitting}
							className={classNames('input--primary full-width', {
								loading: isSubmitting
							})}
							type="text"
							placeholder="Review"
							onChange={event => this.setFormValue('pendingContent', event.target.value)}
							value={pendingContent}
						/>
					</div>
					<div className="col">
						<button
							title="Submit"
							disabled={isSubmitting || isSubmitDisabled}
							className={classNames('button--primary-black min-width', {
								loading: isSubmitting
							})}
							type="submit"
							name="submit">
							Submit
						</button>
					</div>
				</div>
			</form>
		);

		const successState = isSuccess && (
			<div className="bt--gray-border pt8 pb8">
				<h3 className="domaine--medium mb1">
					Thank you for your review!
				</h3>
				<div className="rich-text--page">
					We're reviewing it and should post it shortly.
				</div>
			</div>
		);

		const reviewSummary = bottomLine && (
			<li
				className="list__item">
				<div className="tr">
					<div className="raiting__summary">
						<StarRatings
							rating={bottomLine.product_score}
							numberOfStars={MAX_STARS}
							starRatedColor="#000"
							starEmptyColor="#fff"
							svgIconPath={STAR_SVG_SHAPE}
							svgIconViewBox={STAR_SVG_BOX}
							starDimension={STAR_SIZE}
							starSpacing={STAR_SPACING}
						/>
					</div>
					<p className="sans--xsmall uppercase fw--600 color--gray-text">{bottomLine.total_reviews} {pluralize('Review', bottomLine.total_reviews)}</p>
				</div>
			</li>
		);

		const reviewSummaryMobile = bottomLine && (
			<div className="col c6 hide--sm">
				<div className="tr">
					<StarRatings
						rating={bottomLine.product_score}
						numberOfStars={MAX_STARS}
						starRatedColor="#000"
						starEmptyColor="#fff"
						svgIconPath={STAR_SVG_SHAPE}
						svgIconViewBox={STAR_SVG_BOX}
						starDimension={STAR_SIZE}
						starSpacing={STAR_SPACING}
					/>
				</div>
			</div>
		);

		return (
			<div
				id="reviews"
				className="grid-container contained mt2 mb6 mt8--md mb12--md pt4">
				<div className="row mb4 align--middle">
					<div className="col c6 mb2 mb0--md">
						<h2 className="domaine--large">Reviews</h2>
					</div>
					{reviewSummaryMobile}

					<div className="col c6--sm">
						<ul className="show--sm list list--horizontal tr">
							{reviewSummary}
							<li className="list__item">
								<button
									title="Write a Review"
									onClick={this.toggleForm}
									name="review"
									className="button--primary-black">
									Write a Review
								</button>
							</li>
						</ul>

						<button
							title="Write a Review"
							onClick={this.toggleForm}
							name="review"
							className="button--primary-black hide--sm full-width">
							Write a Review
						</button>
					</div>
				</div>
				{pendingForm}
				{successState}
				{emptyState}
				{reviewRows}
			</div>
		);
	}
}
